/**
 * @file Pure budget-meter math + conversion-funnel event names (#941).
 * @module @labcraft/lab/ui/lib/budget
 *
 * The free tier is a UTC-day VM-time counter; the chrome meter and the
 * out-of-time takeover both render off it. This module holds the small
 * pure helpers they share — remaining-time math, the healthy/low/
 * exhausted classification, the ring fraction, and the concrete LOCAL
 * reset time (the next UTC-day boundary rendered in the learner's
 * timezone). Keeping reset-time derivation here, not on the wire, is
 * deliberate: the boundary is a pure function of the clock (locked
 * decision #3 — a fixed local reset, never "midnight UTC", and never a
 * rolling refill), so the SPA computes it rather than plumbing it
 * through the budget endpoint.
 *
 * Free functions (not class methods), mirroring the sibling
 * `lib/parts_path.ts` pure-helper idiom — these are stateless transforms
 * the meter + takeover import directly.
 */

/**
 * Free-tier "running low" threshold. The meter flips to its warning
 * treatment (and fires the funnel's running-low event) once this little
 * lab time remains. A round 10-minute heads-up — enough lead to find a
 * stopping point or upgrade before the wall.
 */
export const BUDGET_LOW_THRESHOLD_MS = 10 * 60_000

/**
 * Conversion-funnel event names (#941). One declaration so the meter,
 * the takeover, and any future surface fire identical strings — these
 * pair with the backend's authoritative tier-flip (Grafana / #834), not
 * with Umami, which only instruments this SPA-side funnel (drop-off
 * between the three beats), per locked decision #5.
 *
 * @property runningLowShown - The meter first entered its low state.
 * @property outOfTimeShown - The out-of-time takeover rendered.
 * @property upgradeClicked - "Go unlimited" clicked (carries `from`).
 */
export const BUDGET_EVENTS = {
  runningLowShown: 'budget_running_low_shown',
  outOfTimeShown: 'budget_out_of_time_shown',
  upgradeClicked: 'budget_upgrade_clicked',
} as const

/** Coarse meter classification driving copy + color. */
export type BudgetState = 'healthy' | 'low' | 'exhausted'

/**
 * Remaining free-tier ms today. `Infinity` for paid (`limitMs === null`)
 * so a paid caller never renders a finite countdown.
 *
 * @param usedMsToday - `running` ms used this UTC day.
 * @param limitMs - Daily cap, or `null` for unlimited (paid).
 * @returns Non-negative ms remaining, or `Infinity` when unlimited.
 */
export function budgetRemainingMs(
  usedMsToday: number,
  limitMs: number | null,
): number {
  if (limitMs === null) return Infinity
  return Math.max(0, limitMs - usedMsToday)
}

/**
 * Live display-remaining ms: the server snapshot's remaining budget less
 * the `running` time accrued locally since that snapshot landed. The
 * countdown extrapolates from the last `usedMsToday` read rather than
 * refetching every second; {@link useBudget}'s reconcile refetch snaps
 * the baseline back to the authoritative server sum. Clamped ≥ 0.
 *
 * @param snapshotRemainingMs - `budgetRemainingMs` at the last snapshot.
 * @param accruedRunningMs - Wall-clock ms spent `running` since then.
 * @returns Non-negative live remaining ms.
 */
export function budgetLiveRemainingMs(
  snapshotRemainingMs: number,
  accruedRunningMs: number,
): number {
  return Math.max(0, snapshotRemainingMs - accruedRunningMs)
}

/**
 * Reconcile a fresh server budget snapshot against the meter's local
 * accrual, preserving the **monotonic-downward** invariant while the VM
 * runs. Returns the remaining-ms baseline the live countdown should adopt
 * (and accrue from) after the snapshot lands.
 *
 * The live meter extrapolates a countdown from the last server
 * `usedMsToday` while the VM runs (see {@link budgetLiveRemainingMs}). But
 * the server bills VM time only at usage-window boundaries, so a
 * mid-session reconcile routinely returns a STALE — flat, often still-zero
 * — `usedMsToday`. Adopting it verbatim snaps the meter back UP toward the
 * cap every reconcile cadence: the freeze-at-cap reported in #1329. The
 * rule that fixes it:
 *
 * - **First snapshot** (`localRemainingMs === null`, no baseline yet) or
 *   **VM not running** → the authoritative server value governs directly;
 *   there is no live countdown in flight to protect.
 * - **VM running** → accept the server snapshot only when it moves
 *   remaining DOWN (reports more used than local accrual — a window just
 *   billed, or a correction landed). A stale/flat snapshot that would
 *   raise remaining is ignored in favor of the local countdown.
 *
 * Enforcement (the owner-side `maxRunningMs` wall) is unaffected — this
 * governs the DISPLAYED countdown only.
 *
 * @param args - Reconcile inputs.
 * @param args.serverRemainingMs - `budgetRemainingMs(usedMsToday, limitMs)`
 *   for the just-arrived server snapshot.
 * @param args.localRemainingMs - The meter's current live remaining
 *   ({@link budgetLiveRemainingMs}), or `null` when no baseline has been
 *   established yet (first snapshot).
 * @param args.vmRunning - Whether the chapter VM is actively `running`.
 * @returns The remaining-ms baseline to display and accrue from next.
 */
export function budgetReconcileRemainingMs({
  serverRemainingMs,
  localRemainingMs,
  vmRunning,
}: {
  serverRemainingMs: number
  localRemainingMs: number | null
  vmRunning: boolean
}): number {
  // First snapshot, or the VM is idle → the server value governs (no live
  // countdown to protect from a stale re-baseline).
  if (localRemainingMs === null || !vmRunning) return serverRemainingMs

  // Running: honor the server only when it corrects remaining DOWNWARD;
  // never let a stale/flat snapshot snap the countdown back up to the cap.
  return Math.min(serverRemainingMs, localRemainingMs)
}

/**
 * Classify a remaining-ms figure into the meter's three states.
 *
 * @param remainingMs - Output of {@link budgetRemainingMs}.
 * @returns `'exhausted'` at/under zero, `'low'` within the threshold,
 *   else `'healthy'`.
 */
export function budgetState(remainingMs: number): BudgetState {
  if (remainingMs <= 0) return 'exhausted'
  if (remainingMs <= BUDGET_LOW_THRESHOLD_MS) return 'low'
  return 'healthy'
}

/**
 * Whole minutes of lab time left, rounded UP so a learner with any
 * time left never reads "0 min left" (zero is the takeover's job).
 *
 * @param remainingMs - Output of {@link budgetRemainingMs}.
 * @returns Ceil of the remaining minutes.
 */
export function budgetMinutesLeft(remainingMs: number): number {
  return Math.ceil(remainingMs / 60_000)
}

/**
 * The remaining budget as an `M:SS` countdown clock ("9:42", "0:07") —
 * the low-state display where seconds carry the urgency. Minutes are
 * unpadded, seconds zero-padded; rounds UP so any remaining time reads
 * at least "0:01" (zero is the exhausted takeover's job), mirroring
 * {@link budgetMinutesLeft}.
 *
 * @param remainingMs - Live remaining ms.
 * @returns An `M:SS` clock string.
 */
export function formatBudgetClock(remainingMs: number): string {
  const totalSec = Math.max(0, Math.ceil(remainingMs / 1000))
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

/**
 * Fraction of the daily budget still available for a known remaining-ms
 * figure — the live variant that drives the ring while the countdown
 * ticks. Clamped to `[0, 1]`; `1` for paid / a non-positive limit.
 *
 * @param remainingMs - Live remaining ms (from {@link budgetLiveRemainingMs}).
 * @param limitMs - Daily cap, or `null` for unlimited.
 * @returns Remaining fraction in `[0, 1]`.
 */
export function budgetFractionRemaining(
  remainingMs: number,
  limitMs: number | null,
): number {
  if (limitMs === null || limitMs <= 0) return 1
  return Math.max(0, Math.min(1, remainingMs / limitMs))
}

/**
 * Fraction of the daily budget still available, clamped to `[0, 1]` —
 * drives the meter ring's filled arc from the raw server snapshot.
 * Returns `1` for paid / a non-positive limit (the ring reads full,
 * though paid never renders).
 *
 * @param usedMsToday - `running` ms used this UTC day.
 * @param limitMs - Daily cap, or `null` for unlimited.
 * @returns Remaining fraction in `[0, 1]`.
 */
export function budgetFraction(
  usedMsToday: number,
  limitMs: number | null,
): number {
  if (limitMs === null || limitMs <= 0) return 1
  return budgetFractionRemaining(limitMs - usedMsToday, limitMs)
}

/**
 * The next UTC-day boundary — the instant the free budget resets.
 *
 * @param now - Current time.
 * @returns A `Date` at the next `00:00:00.000Z`.
 */
export function nextBudgetReset(now: Date): Date {
  const reset = new Date(now.getTime())
  reset.setUTCHours(24, 0, 0, 0)
  return reset
}

/**
 * The reset boundary as a concrete LOCAL clock string ("5:00 PM") —
 * locked decision #3's "resets 5:00 PM your time", never "midnight UTC".
 *
 * @param reset - Output of {@link nextBudgetReset}.
 * @returns A localized `h:mm AM/PM`-style clock string.
 */
export function formatResetClock(reset: Date): string {
  return reset.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

/**
 * Time until the reset as a compact countdown ("6h 12m" / "12m") — the
 * supplementary "(in …)" the takeover shows beside the fixed local
 * reset time. Minutes are zero-padded when an hours segment precedes
 * them.
 *
 * @param reset - Output of {@link nextBudgetReset}.
 * @param now - Current time.
 * @returns A `Xh YYm` / `Ym` countdown string (never negative).
 */
export function formatResetCountdown(reset: Date, now: Date): string {
  const ms = Math.max(0, reset.getTime() - now.getTime())
  const totalMin = Math.round(ms / 60_000)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  if (h <= 0) return `${m}m`
  return `${h}h ${String(m).padStart(2, '0')}m`
}
